import { connect } from "react-redux";
import { useEffect, useState } from "react";
import { withNamespaces } from "react-i18next";
import { AvForm, AvField } from "availity-reactstrap-validation";
import { EditorState } from "draft-js";
import DatePicker from "react-datepicker";
import * as datePickerLocales from "date-fns/locale";
import { INTERNATIONAL_DATE_FORMAT } from "src/components/constants";
import {
    Button,
    Card,
    CardBody,
    Col,
    FormGroup,
    Label,
    Row
} from "reactstrap";
import Select from "react-select";
import { Editor } from "react-draft-wysiwyg";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Spinner from 'react-bootstrap/Spinner';
import {
  CompanySizes,
  ThirdPartyCategories
} from "src/modules/3rd-party-management/constants";
import ThirdpartyUsersService from "src/modules/3rd-party-management/apis/common/ThirdpartyUsersService";
import IndustriesService from "src/modules/3rd-party-management/apis/common/IndustriesService";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";
import {
    resolveThirdPartyCategoryForForm
} from "src/modules/3rd-party-management/helpers/thirdPartyCategory";

const filterOptions = (
    candidate,
    input
) => {
    if (input) {
        const candidateInput = candidate.label.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, "");
        const normalizedInput = input.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, "");

        return candidateInput.indexOf(normalizedInput) >= 0 || ((candidate.data.keywords || []).findIndex((keyword) => {
            const normalizedKeyword = keyword.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, "");

            return normalizedKeyword.indexOf(normalizedInput) >= 0
        }) >= 0);
    }

    return true;
};

const CompanyDetails = ({
    t,
    lng,
    thirdPartyType,
    defaultValues,
    setCompanyDetailsTabCompleted,
    memoizedEntitiesList,
    memoizedCountriesList,
    isLoading
}) => {
    const initialSubType = resolveThirdPartyCategoryForForm(defaultValues?.subType, thirdPartyType, defaultValues?.customSubType);

    const [ selectedIndusries, setSelectedIndustries ] = useState(defaultValues?.industries || []);
    const [ legalEntities, setLegalEntities ] = useState(defaultValues?.entities || []);
    const [ description, setDescription ] = useState(defaultValues?.description || EditorState.createEmpty());
    const [ selectedSubType, setSelectedSubType ] = useState(initialSubType.selectValue);
    const [industriesQuery, setIndustriesQuery] = useState({
        isLoading: false,   
        itemsCount: 0,
        pageIndex: 1,
        pageSize: 1000,
        searchText: '',
        items: []
    });

    const [ birthDate, setBirthDate ] = useState(defaultValues?.individual_date_of_birth ? new Date(defaultValues.individual_date_of_birth) : null);
    const [ birthCountry, setBirthCountry ] = useState(defaultValues?.individual_birth_place_country?.value ? defaultValues.individual_birth_place_country : null)

    const {
        mutate      :   checkThirdpartyExistenceMutation,
        isLoading   :   checkingThirdpartyExistenceInProcess
    } = useMutation({
        mutationFn: async (payload) => {
            const service = ThirdpartyUsersService.getInstance();

            return await service.checkThirdpartyExistence(payload);
        }
    });

    const handleFetchIndustriesListQuery = useQuery({
        queryKey: [
            '3rd-party-management-search-industries-query',
            lng
        ],
        queryFn: async () => {
            const service = IndustriesService.getInstance();

            setIndustriesQuery((currentState) => {
                return {
                    ...currentState,
                    isLoading: true
                }
            });

            return await service.fetchList({
                language: lng !== 'en' ? lng : undefined,
                pageSize: industriesQuery.pageSize,
                pageIndex: industriesQuery.pageIndex,
                name: industriesQuery.searchText || undefined
            });
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while fetching industries list.'), {
                type: 'error',
            });
        },
        onSuccess: (response) => {
            setIndustriesQuery((currentState) => {
                return {
                    ...currentState,
                    isLoading: false,
                    // itemsCount: parseInt(response.itemsCount),
                    // pageIndex: parseInt(response.pageIndex),
                    // pageSize: parseInt(response.pageSize),
                    items: (response || []).map((industry) => {
                        return {
                            value : industry.id,
                            label : `${industry.class} - ${industry.title}`,
                            keywords: industry?.keywords || []
                        }
                    })
                }
            });
        },
        onSettled: () => {
            setIndustriesQuery((currentState) => {
                return {
                    ...currentState,
                    isLoading: false
                }
            });
        }
    });

    useEffect(() => {
        if(selectedIndusries.length && industriesQuery.items.length){
            const result = selectedIndusries.map((sIndustry) => {
                const translatedItem = industriesQuery.items.find((listItem) => listItem.value === sIndustry.value);

                return translatedItem || sIndustry
            });

            setSelectedIndustries(result);
        }
    }, [
        t,
        industriesQuery.items
    ]);

    useEffect(() => {
        const timeOutId = setTimeout(() => {
            handleFetchIndustriesListQuery.refetch();
        }, 500);

        return () => clearTimeout(timeOutId);
    }, [ industriesQuery.searchText ]);
    
    const onValidSubmit = (_e, values) => {
        const { subTypeOther, ...restValues } = values;

        checkThirdpartyExistenceMutation({
            name        :   values.company_name,
            websiteUrl  :   values.website,
            vatNumber   :   values.vat_number || undefined
        }, {
            onSuccess: () => {
                setCompanyDetailsTabCompleted({
                    ...restValues,
                    subType: values.subType,
                    customSubType: values.subType === 'other' ? subTypeOther : undefined,
                    entities: legalEntities,
                    description: description
                })
            },
            onError: (error) => {
                if(error.response?.data?.error === 'invalid_vatNumber'){
                    toast(t('A company with this VAT ID already exists.'), {
                        type: "error",
                    });
                }
                else{
                    toast(t('A company with this name already exists.'), {
                        type: "error",
                    });
                }
            }
        })
    }

    const sizes = [];

    for(const sizeKey in CompanySizes){
        sizes.push({
            value : sizeKey,
            label : `${CompanySizes[sizeKey]?.title} ${t("Employees")}`
        });
    }

    const renderIndividualTypeBody = () => {
        const lgSize = selectedSubType === 'other' ? "3" : "3";

        return (
            <CardBody className="p-0">
                <p className="sub-header medium">
                    {t("Personal Details")}
                </p>

                <Row>
                    <Col lg={lgSize} md="6" sm="12" className="mb-4">
                        <Label for="third_party_category">
                            {t('Third Party Category')}: <span className="text-danger">*</span>
                        </Label>
                        <AvField
                            id="third_party_category"
                            name="subType"
                            type="select"
                            errorMessage={t("This field cannot be blank")}
                            validate={{
                                required: { value: true }
                            }}
                            defaultValue={initialSubType.selectValue}
                            onChange={(e) => setSelectedSubType(e.target.value)}
                        >
                            <option>
                                {t('Select...')}
                            </option>
                            {ThirdPartyCategories[thirdPartyType].map((tCat) => {
                                return (
                                    <option 
                                        value={tCat.value}
                                    >
                                        {t(tCat.label)}
                                    </option>
                                )
                            })}
                        </AvField>
                    </Col>

                    <Col lg={lgSize} md="6" sm="12" className="mb-4" hidden={selectedSubType !== 'other'}>
                        <Label for="sub_type_other">
                            {t('Other Category')}: <span className="text-danger">*</span>
                        </Label>
                        <AvField
                            id="sub_type_other"
                            name="subTypeOther"
                            placeholder=""
                            type="text"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: selectedSubType === 'other' },
                            }}
                            defaultValue={initialSubType.otherText}
                        />
                    </Col>

                    <Col lg={lgSize} md="6" sm="12" className="mb-4">
                        <Label for="company_name">
                            {t("Name")}: <span className="text-danger">*</span>
                        </Label>

                        <AvField
                            name="company_name"
                            placeholder=""
                            type="text"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: true },
                            }}
                            defaultValue={defaultValues?.company_name}
                            id="company_name"
                        />
                    </Col>

                    <Col lg={lgSize} md="6" sm="12" className="mb-4">
                        <Label for="company_name">
                            {t("Nickname / AKA")}: 
                        </Label>

                        <AvField
                            name="company_shortname"
                            placeholder=""
                            type="text"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: false }
                            }}
                            defaultValue={defaultValues?.company_shortname}
                            id="company_shortname"
                        />
                    </Col>

                    <Col lg={lgSize} md="6" sm="12" className="mb-4">
                        <Label for="individual_date_of_birth">
                            {t("Date of Birth")}: 
                        </Label>

                        <DatePicker
                            locale={datePickerLocales[lng]}
                            name="deadline"
                            id="deadline"
                            className="form-control"
                            selected={birthDate}
                            showTimeSelect={false}
                            dateFormat={INTERNATIONAL_DATE_FORMAT}
                            onChange={(date) => {
                                setBirthDate(date)
                            }}
                        /> 

                        <AvField
                            id="individual_date_of_birth"
                            name="individual_date_of_birth"
                            placeholder=""
                            type="hidden"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: false },
                            }}
                            value={birthDate}
                        />
                    </Col>
                    
                    <Col lg={lgSize} md="6" sm="12" className="mb-4">
                        <Label for="individual_birth_place_country">
                            {t("Country of Birth")}: 
                        </Label>
                        <Select
                            placeholder={t("Select...")}
                            classNamePrefix="select2-selection"
                            options={memoizedCountriesList}
                            menuPortalTarget={document.body}
                            onChange={(e) => {
                                setBirthCountry(e)
                            }}
                            value={birthCountry}
                        />
                        <AvField
                            id="individual_birth_place_country"
                            name="individual_birth_place_country"
                            placeholder=""
                            type="hidden"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: false },
                            }}
                            value={birthCountry}
                        />
                    </Col>

                    <Col lg={lgSize} md="6" sm="12" className="mb-4">
                        <Label for="individual_tax_id">
                            {t("Tax ID")}: 
                        </Label>
                        <AvField
                            id="individual_tax_id"
                            name="individual_tax_id"
                            placeholder=""
                            type="text"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: false },
                            }}
                            defaultValue={defaultValues?.individual_tax_id}
                        />
                    </Col>

                    <Col lg={lgSize} md="6" sm="12" className="mb-4">
                        <Label for="individual_national_id_number">
                            {t("National ID Number")}: 
                        </Label>
                        <AvField
                            id="individual_national_id_number"
                            name="individual_national_id_number"
                            placeholder=""
                            type="text"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: false },
                            }}
                            defaultValue={defaultValues?.individual_national_id_number}
                        />
                    </Col>

                    {isFeatureEnabledForActiveModule('entity_management') && (
                        <Col lg="12" className="mb-4">
                            <Row>
                                <Col lg={6}>
                                    <Label for="connected-legal-entities">
                                        {t("Connected Legal Entities")}: 
                                    </Label>

                                    <Select
                                        isMulti
                                        placeholder={t("Select") + "..."}
                                        classNamePrefix="select2-selection"
                                        id="connected-legal-entities"
                                        options={memoizedEntitiesList}
                                        menuPortalTarget={document.body}
                                        onChange={(e) => {
                                            setLegalEntities(e)
                                        }}
                                        value={legalEntities}
                                        filterOption={filterOptions}
                                    />
                                </Col>
                            </Row>
                        </Col>
                    )}

                    <Col sm="12">
                        <Label for="description">
                            {t("Description")}: 
                        </Label>
                        <Editor
                            editorState={description}
                            toolbarClassName="toolbarClassName"
                            wrapperClassName="wrapperClassName"
                            editorClassName="editorClassName"
                            onEditorStateChange={(e) => setDescription(e)}
                            toolbar={{
                                options: [
                                    "inline",
                                    "blockType",
                                    "fontSize",
                                    "list",
                                    "textAlign",
                                    "colorPicker",
                                    "link",
                                    "remove",
                                    "history",
                                ],
                                inline: {
                                    options: [
                                    "bold",
                                    "italic",
                                    "underline",
                                    "strikethrough",
                                    "monospace",
                                    ],
                                    bold: { className: "bordered-option-classname" },
                                    italic: { className: "bordered-option-classname" },
                                    underline: { className: "bordered-option-classname" },
                                    strikethrough: {
                                    className: "bordered-option-classname",
                                    },
                                    code: { className: "bordered-option-classname" },
                                },
                                blockType: {
                                    className: "bordered-option-classname",
                                },
                                fontSize: {
                                    className: "bordered-option-classname",
                                }
                            }}
                        />
                    </Col>
                </Row>
            </CardBody>
        )
    }

    const renderCompanyTypeBody = () => {
        return (
            <CardBody className="p-0">
                <p className="sub-header medium">
                    {t("General Information")}
                </p>

                <Row>
                    <Col lg={selectedSubType === 'other' ? "3" : "3"} md="6" sm="12" className="mb-4">
                        <Label for="third_party_category">
                            {t('Third Party Category')}: <span className="text-danger">*</span>
                        </Label>
                        <AvField
                            id="third_party_category"
                            name="subType"
                            type="select"
                            errorMessage={t("This field cannot be blank")}
                            validate={{
                                required: { value: true }
                            }}
                            defaultValue={initialSubType.selectValue}
                            onChange={(e) => setSelectedSubType(e.target.value)}
                        >
                            <option>
                                {t('Select...')}
                            </option>
                            {ThirdPartyCategories[thirdPartyType].map((tCat) => {
                                return (
                                    <option 
                                        value={tCat.value}
                                    >
                                        {t(tCat.label)}
                                    </option>
                                )
                            })}
                        </AvField>
                    </Col>

                    <Col lg="3" md="6" sm="12" className="mb-4" hidden={selectedSubType !== 'other'}>
                        <Label for="sub_type_other">
                            {t('Other Category')}: <span className="text-danger">*</span>
                        </Label>
                        <AvField
                            id="sub_type_other"
                            name="subTypeOther"
                            placeholder=""
                            type="text"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: selectedSubType === 'other' },
                            }}
                            defaultValue={initialSubType.otherText}
                        />
                    </Col>

                    <Col lg="3" md="6" sm="12" className="mb-4">
                        <Label for="company_name">
                            {t("Company Name")}: <span className="text-danger">*</span>
                        </Label>

                        <AvField
                            name="company_name"
                            placeholder=""
                            type="text"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: true },
                            }}
                            defaultValue={defaultValues?.company_name}
                            id="company_name"
                        />
                    </Col>

                    <Col lg="3" md="6" sm="12" className="mb-4">
                        <Label for="company_name">
                            {t("Short Name")}: 
                        </Label>

                        <AvField
                            name="company_shortname"
                            placeholder=""
                            type="text"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: false }
                            }}
                            defaultValue={defaultValues?.company_shortname}
                            id="company_shortname"
                        />
                    </Col>

                    <Col lg="3" md="6" sm="12" className="mb-4">
                        <Label for="selected-industries">
                            {t("Industries")}: <span className="text-danger">*</span>
                        </Label>

                        <Select
                            isLoading={industriesQuery.isLoading}
                            placeholder={t("Select") + "..."}
                            classNamePrefix="select2-selection"
                            id='selected-industries'
                            options={industriesQuery.items}
                            menuPortalTarget={document.body}
                            onChange={(e) => setSelectedIndustries(e)}
                            value={selectedIndusries}
                            isMulti
                            filterOption={(e) => e}
                            onInputChange={(input) => {
                                setIndustriesQuery((currentState) => {
                                    return {
                                        ...currentState,
                                        searchText: input
                                    }
                                })
                            }}
                        />

                        <AvField
                            name="industries"
                            type="hidden"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: true },
                            }}
                            value={selectedIndusries}
                        />
                    </Col>
                </Row>

                <Row>
                    <Col lg="3" md="6" sm="12" className="mb-4">
                        <Label for="vat_number">
                            {t("VAT ID")}: 
                        </Label>
                        <AvField
                            id="vat_number"
                            name="vat_number"
                            placeholder=""
                            type="text"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: false },
                            }}
                            defaultValue={defaultValues?.vat_number}
                        />
                    </Col>

                    <Col lg="3" md="6" sm="12" className="mb-4">
                        <Label for="company_size">
                            {t("Company Size")}: <span className="text-danger">*</span>
                        </Label>
                        <AvField
                            id="company_size"
                            placeholder={t('Select...')}
                            name="company_size"
                            type="select"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: true },
                            }}
                            defaultValue={defaultValues?.company_size || '0-10'}
                        >
                            {sizes.map((size, index) => {
                                return (
                                    <option key={index} value={size.value}>
                                        {size.label}
                                    </option>
                                )
                            })}
                        </AvField>
                    </Col>

                    <Col lg="3" md="6" sm="12" className="mb-4">
                        <Label for="brands">
                            {t("Brands")}:
                        </Label>
                        <AvField
                            id="brands"
                            name="brands"
                            placeholder=""
                            type="text"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: false },
                            }}
                            defaultValue={defaultValues?.brands}
                        />
                    </Col>

                    <Col lg={'3'} md="6" sm="12" className="mb-4">
                        <Label for="website">
                            {t("Website")}: 
                        </Label>
                        <AvField
                            name="website"
                            id="website"
                            type="text"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: false },
                                pattern: {
                                    value: /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/\S*)?$/,
                                    errorMessage: t("Please enter a valid URL"),
                                },
                            }}
                            defaultValue={defaultValues?.website}
                        />
                    </Col>

                    {isFeatureEnabledForActiveModule('entity_management') && (
                        <Col lg="6" md="12" sm="12" className="mb-4">
                            <Label for="connected-legal-entities">
                                {t("Connected Legal Entities")}: 
                            </Label>

                            <Select
                                isMulti
                                placeholder={t("Select") + "..."}
                                classNamePrefix="select2-selection"
                                id="connected-legal-entities"
                                options={memoizedEntitiesList}
                                menuPortalTarget={document.body}
                                onChange={(e) => {
                                    setLegalEntities(e)
                                }}
                                value={legalEntities}
                                filterOption={filterOptions}
                            />
                        </Col>
                    )}
                </Row>

                <Row>
                    <Col>
                        <Label for="description">
                            {t("Description")}: 
                        </Label>
                        <Editor
                            editorState={description}
                            toolbarClassName="toolbarClassName"
                            wrapperClassName="wrapperClassName"
                            editorClassName="editorClassName"
                            onEditorStateChange={(e) => setDescription(e)}
                            toolbar={{
                                options: [
                                    "inline",
                                    "blockType",
                                    "fontSize",
                                    "list",
                                    "textAlign",
                                    "colorPicker",
                                    "link",
                                    "remove",
                                    "history",
                                ],
                                inline: {
                                    options: [
                                    "bold",
                                    "italic",
                                    "underline",
                                    "strikethrough",
                                    "monospace",
                                    ],
                                    bold: { className: "bordered-option-classname" },
                                    italic: { className: "bordered-option-classname" },
                                    underline: { className: "bordered-option-classname" },
                                    strikethrough: {
                                    className: "bordered-option-classname",
                                    },
                                    code: { className: "bordered-option-classname" },
                                },
                                blockType: {
                                    className: "bordered-option-classname",
                                },
                                fontSize: {
                                    className: "bordered-option-classname",
                                }
                            }}
                        />
                    </Col>
                </Row>
            </CardBody>
        )
    }

    const renderThirdPartyBody = () => {
        if (thirdPartyType === 'company')
            return renderCompanyTypeBody()
        return renderIndividualTypeBody()
    }

    const renderOwnCompanyBody = () => {
        return (
            <CardBody className="p-0">
                <p className="sub-header medium">
                    {t("General Information")}
                </p>

                <Row>
                    <Col lg="3" md="6" sm="12" className="mb-4">
                        <Label for="industry">
                            {t("Legal Entity")}: <span className="text-danger">*</span>
                        </Label>

                        <Select
                            placeholder={t("Select") + "..."}
                            classNamePrefix="select2-selection"
                            id="legal-entities"
                            options={memoizedEntitiesList}
                            menuPortalTarget={document.body}
                            onChange={(e) => {
                                setLegalEntities(e)

                                if(e.naceCode?.id){
                                    const translatedIndustry = (industriesQuery.items || []).find((industryItem) => {
                                        return industryItem.value === e.naceCode.id
                                    })

                                    setSelectedIndustries([{
                                        value: e.naceCode.id,
                                        label: translatedIndustry ? 
                                            translatedIndustry.label :
                                            `${e.naceCode.product_group} - ${e.naceCode.activity}` 
                                    }])
                                }
                            }}
                            value={legalEntities}
                            filterOption={filterOptions}
                        />

                        <AvField
                            name="company_name"
                            type="hidden"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: true },
                            }}
                            id="company_name"
                            value={legalEntities?.label}
                        />
                    </Col>

                    <Col lg="3" md="6" sm="12" className="mb-4">
                        <Label for="company_short_name">
                            {t("Short Name")}: 
                        </Label>
                        <AvField
                            name="company_short_name"
                            id="company_short_name"
                            disabled
                            type="text"
                            className="form-control"
                            value={legalEntities?.shortName}
                        />
                    </Col>

                    <Col lg="3" md="6" sm="12" className="mb-4">
                        <Label for="brands">
                            {t("Brands")}: 
                        </Label>
                        <AvField
                            name="brands"
                            id="brands"
                            type="text"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: false },
                            }}
                            defaultValue={defaultValues?.brands}
                        />
                    </Col>

                    <Col lg="3" md="6" sm="12" className="mb-4">
                        <Label for="vat_number">
                            {t("VAT ID")}: 
                        </Label>
                        <AvField
                            name="vat_number"
                            id="vat_number"
                            type="text"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: false },
                            }}
                            defaultValue={defaultValues?.vat_number}
                        />
                    </Col>
                </Row>

                <Row>
                    <Col lg="3" md="6" sm="12" className="mb-4">
                        <Label for="selected-industries">
                            {t("Industries")}: <span className="text-danger">*</span>
                        </Label>

                        <Select
                            id="selected-industries"
                            isLoading={ industriesQuery.isLoading }
                            placeholder={t("Select") + "..."}
                            classNamePrefix="select2-selection"
                            options={industriesQuery.items}
                            menuPortalTarget={document.body}
                            onChange={(e) => setSelectedIndustries(e)}
                            value={selectedIndusries}
                            isMulti
                            filterOption={(e) => e}
                            onInputChange={(input) => {
                                setIndustriesQuery((currentState) => {
                                    return {
                                        ...currentState,
                                        searchText: input
                                    }
                                })
                            }}
                        />

                        <AvField
                            name="industries"
                            type="hidden"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: true },
                            }}
                            value={selectedIndusries}
                        />
                    </Col>

                    <Col lg="3" md="6" sm="12" className="mb-4">
                        <Label for="company_size">
                            {t("Company Size")}: <span className="text-danger">*</span>
                        </Label>
                        <AvField
                            id="company_size"
                            placeholder={t('Select...')}
                            name="company_size"
                            type="select"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: true },
                            }}
                            defaultValue={defaultValues?.company_size || '0-10'}
                        >
                            {sizes.map((size, index) => {
                                return (
                                    <option key={index} value={size.value}>
                                        {size.label}
                                    </option>
                                )
                            })}
                        </AvField>
                    </Col>

                    <Col lg="6" md="6" sm="12" className="mb-4">
                        <Label for="website">
                            {t("Website")}: 
                        </Label>

                        <AvField
                            name="website"
                            type="text"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: false },
                                pattern: {
                                    value: /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/\S*)?$/,
                                    errorMessage: t("Please enter a valid URL"),
                                },
                            }}
                            id="website"
                            defaultValue={defaultValues?.website}
                        />
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <Label for="description">
                            {t("Description")}: 
                        </Label>

                        <Editor
                            editorState={description}
                            toolbarClassName="toolbarClassName"
                            wrapperClassName="wrapperClassName"
                            editorClassName="editorClassName"
                            onEditorStateChange={(e) => setDescription(e)}
                            toolbar={{
                                options: [
                                    "inline",
                                    "blockType",
                                    "fontSize",
                                    "list",
                                    "textAlign",
                                    "colorPicker",
                                    "link",
                                    "remove",
                                    "history",
                                ],
                                inline: {
                                    options: [
                                    "bold",
                                    "italic",
                                    "underline",
                                    "strikethrough",
                                    "monospace",
                                    ],
                                    bold: { className: "bordered-option-classname" },
                                    italic: { className: "bordered-option-classname" },
                                    underline: { className: "bordered-option-classname" },
                                    strikethrough: {
                                    className: "bordered-option-classname",
                                    },
                                    code: { className: "bordered-option-classname" },
                                },
                                blockType: {
                                    className: "bordered-option-classname",
                                },
                                fontSize: {
                                    className: "bordered-option-classname",
                                }
                            }}
                        />
                    </Col>
                </Row>
            </CardBody>
        )
    }

    if(isLoading){
        return (
            <div className="d-flex justify-content-center align-items-center">
                <Spinner size='sm' className="me-1" color="primary" />
                {t('Loading...')}
            </div>
        )
    }

    return (
        <>
            <AvForm
                className="needs-validation"
                onValidSubmit={onValidSubmit}
                id="company_details"
                autocomplete="off"
            >
                <Card className="wizard-steps-content-container">
                    {thirdPartyType === 'own' ? (
                        <>
                            {renderOwnCompanyBody()}
                        </>
                    ) : (
                        <>
                            {renderThirdPartyBody()}
                        </>
                    )}
                </Card>

                <Row className="mt-2 mb-4">
                    <FormGroup
                        style={{
                            display: "flex",
                            flexFlow: "row wrap",
                            justifyContent: "flex-end",
                        }}
                        className="actions-buttons-container"
                    >
                        <Button disabled={checkingThirdpartyExistenceInProcess} color="primary">
                            {checkingThirdpartyExistenceInProcess && (
                                <Spinner className="me-2" animation="border" variant="white" size="sm"/>
                            )}
                            {t("Next")}
                        </Button>
                    </FormGroup>
                </Row>
            </AvForm>
        </>
    );
};

const mapStateToProps = (state) => {
	return {
		modules: state.Modules
	};
};

export default withNamespaces()(
    connect(mapStateToProps, null)(
        CompanyDetails
    )
);